import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  CoverageGapIllustration,
  TimeOffConflictIllustration,
  PrintFriendlyIllustration,
  CanadaHostedIllustration,
  FastSetupIllustration,
} from '@/components/illustrations';

/**
 * Sign-in page (Day 14.5 — V1 marketing landing layout, with illustrations).
 *
 * Two-pane layout:
 *   - Left (60% on desktop): rotating illustrated cards, how-it-works,
 *     trust strip, founder note.
 *   - Right (40% on desktop): the sign-in form + "Start your pilot" CTA + FAQ.
 *
 * Mobile stacks form-first, marketing content below.
 *
 * Cards rotate automatically every 6 seconds. Dot indicators double as
 * click-to-jump navigation (auto-rotate pauses on user interaction).
 *
 * Illustrations live in @/components/illustrations so the public Landing
 * page can render the exact same brand SVGs without duplicating the source.
 */

interface Card {
  title: string;
  body: string;
  illustration: () => ReactNode;
}

const CARDS: Card[] = [
  {
    title: 'Every shift covered. Every day. Every month.',
    body: 'Build the month visually — drag, drop, done. Coverage gaps surface in amber as you go, not at 8am Saturday morning when a customer is already at the counter.',
    illustration: CoverageGapIllustration,
  },
  {
    title: 'Approve time off without breaking the schedule',
    body: 'PharmaSync flags every shift the request would collide with the moment you open it. You decide with the full picture, not after.',
    illustration: TimeOffConflictIllustration,
  },
  {
    title: 'Print the month in a single page, every time',
    body: 'Letter landscape, ready to print or save as PDF. Post it on the back-room wall, email it to staff who don\'t want app access, file it in the binder behind the counter.',
    illustration: PrintFriendlyIllustration,
  },
  {
    title: 'Made for Canadian pharmacies, audited under Canadian privacy law',
    body: 'For pharmacies: pharmacist-on-duty rules, all 11 Canadian timezones, retention policies your regulator expects. For privacy: hosted in Toronto, PIPEDA-aware, audit log on every change.',
    illustration: CanadaHostedIllustration,
  },
  {
    title: 'Set up your first month in one sitting',
    body: 'Three steps — add locations, invite staff, drag shifts. The Excel file and the group chat both retire. No consultant, no training day, no 50-page PDF.',
    illustration: FastSetupIllustration,
  },
];

const HOW_IT_WORKS = [
  { num: '1', title: 'Add your locations', body: 'Hours, address, contact. ~2 min per pharmacy.' },
  { num: '2', title: 'Build the schedule', body: 'Drag shifts onto a month grid. Conflicts flagged live.' },
  { num: '3', title: 'Publish to staff', body: 'Magic-link invites. Staff see their shifts on next sign-in.' },
];

const TRUST_ITEMS = [
  { title: 'Hosted in Canada', body: 'ca-central-1 region' },
  { title: 'Audit-logged', body: 'Every change recorded' },
  { title: 'Print-friendly', body: 'Single-page month view' },
];

const FAQ_ITEMS = [
  {
    q: 'Is my data safe?',
    a: 'Encrypted in transit and at rest. Hosted on Canadian Supabase + AWS infrastructure. Every change to your data is recorded in an owner-only audit log. PIPEDA-aware. PHIPA does not apply since this is workforce data, not patient health information.',
  },
  {
    q: 'What if I cancel?',
    a: 'Export everything (staff, schedules, audit log) on the way out. We delete or anonymize within 90 days. No lock-in. No "win-back" emails.',
  },
  {
    q: 'Can my staff see what I see?',
    a: 'No. Staff see their own shifts and their own time-off requests. Managers see the schedule for their assigned locations. Only owners see the full audit log and admin surfaces.',
  },
];

const FOUNDER_NOTE =
  'I built PharmaSync after watching too many Canadian pharmacies lose evenings and weekends to Excel sheets and group-chat schedule swaps. Scheduling shouldn’t be the hardest part of running a pharmacy. — Jason Al-Farisi';

const PILOT_DISCLAIMER =
  'Currently in private pilot — pricing is finalised after the pilot phase. We work closely with our first pharmacies to make sure we got it right.';

const ROTATE_INTERVAL_MS = 6000;

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flash messages passed via route state from other auth pages.
  // - ResetPassword: "Password updated. Sign in with your new password."
  // - AuthCallback: error string when verification link is invalid/expired
  // We capture once on mount and clear history.state so a refresh doesn't
  // replay the message.
  // Flash messages come from two channels:
  //   1. React Router route state (soft nav from inside the app)
  //   2. sessionStorage 'auth_flash' (set before a hard reload, e.g. after
  //      password reset where we MUST fully reset the identity store).
  const sessionFlash = (() => {
    try { return sessionStorage.getItem('auth_flash'); } catch { return null; }
  })();
  const [flashMessage, setFlashMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? sessionFlash ?? null
  );
  const [flashError, setFlashError] = useState<string | null>(
    (location.state as { error?: string } | null)?.error ?? null
  );
  useEffect(() => {
    try { sessionStorage.removeItem('auth_flash'); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (flashMessage || flashError) {
      window.history.replaceState({}, document.title);
    }
  }, [flashMessage, flashError]);

  const [cardIdx, setCardIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Auto-advance the card carousel. Pauses while hovered or after a manual jump.
  useEffect(() => {
    if (paused) return;
    const handle = setInterval(() => {
      setCardIdx((i) => (i + 1) % CARDS.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [paused]);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Email and password are both required.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        setError(error.message);
        return;
      }
      navigate(from, { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  const card = CARDS[cardIdx];
  const Illustration = card.illustration;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-16 items-start">

          {/* LEFT: marketing content */}
          <div className="lg:col-span-3 space-y-10 order-2 lg:order-1">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="" className="h-10 w-10" aria-hidden="true" />
              <div>
                <h1 className="text-2xl font-semibold tracking-tight leading-none">
                  <span style={{ color: 'hsl(var(--brand-navy))' }}>Pharma</span>
                  <span style={{ color: 'hsl(var(--brand-teal))' }}>Sync</span>
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Scheduling and time-off for Canadian pharmacies.
                </p>
              </div>
            </div>

            {/* Carousel */}
            <section
              aria-label="Product highlights"
              aria-live="polite"
              className="rounded-lg border border-border p-6 space-y-4"
              style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.10)' }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="flex justify-center">
                <Illustration />
              </div>
              <h2
                className="text-xl font-semibold leading-snug"
                style={{ color: 'hsl(var(--brand-navy))' }}
              >
                {card.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {card.body}
              </p>
              <div className="flex justify-center pt-2">
                <div className="flex gap-1.5" role="tablist" aria-label="Highlight selector">
                  {CARDS.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={i === cardIdx}
                      aria-label={`Show highlight ${i + 1}`}
                      onClick={() => { setPaused(true); setCardIdx(i); }}
                      className="h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      style={{
                        width: i === cardIdx ? '24px' : '8px',
                        backgroundColor: i === cardIdx
                          ? 'hsl(var(--brand-navy))'
                          : 'hsl(var(--brand-navy) / 0.25)',
                      }}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* How it works */}
            <section aria-labelledby="how-it-works-h">
              <h2 id="how-it-works-h" className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-4">
                How it works
              </h2>
              <ol className="grid gap-4 sm:grid-cols-3">
                {HOW_IT_WORKS.map((step) => (
                  <li
                    key={step.num}
                    className="rounded-lg border border-border bg-card p-4 space-y-1"
                  >
                    <div
                      className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold"
                      style={{
                        backgroundColor: 'hsl(var(--brand-navy))',
                        color: 'white',
                      }}
                    >
                      {step.num}
                    </div>
                    <p className="text-sm font-medium pt-1">{step.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.body}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* Trust strip */}
            <section aria-label="Trust signals" className="flex flex-wrap gap-3">
              {TRUST_ITEMS.map((item) => (
                <div
                  key={item.title}
                  className="flex-1 min-w-[140px] rounded-md border border-border px-3 py-2 text-xs"
                  style={{ backgroundColor: 'hsl(var(--brand-navy) / 0.04)' }}
                >
                  <p className="font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>
                    {item.title}
                  </p>
                  <p className="text-muted-foreground mt-0.5">{item.body}</p>
                </div>
              ))}
            </section>

            {/* Founder note + pilot disclaimer */}
            <div className="space-y-3 pt-2">
              <p className="text-sm italic text-muted-foreground border-l-2 pl-3"
                 style={{ borderColor: 'hsl(var(--brand-teal))' }}>
                {FOUNDER_NOTE}
              </p>
              <p className="text-xs text-muted-foreground">
                {PILOT_DISCLAIMER}
              </p>
            </div>
          </div>

          {/* RIGHT: sign-in form + FAQ */}
          <div className="lg:col-span-2 space-y-6 order-1 lg:order-2">
            <div className="rounded-lg border border-border bg-card p-6 space-y-5">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold tracking-tight">
                  Sign in to PharmaSync
                </h2>
                <p className="text-xs text-muted-foreground">
                  Use the email your account was created with.
                </p>
              </div>

              {flashMessage && (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-md border p-3 text-sm flex items-start gap-2"
                  style={{
                    borderColor: 'hsl(var(--brand-green) / 0.4)',
                    backgroundColor: 'hsl(var(--brand-green) / 0.1)',
                    color: 'hsl(var(--brand-navy))',
                  }}
                >
                  <span aria-hidden="true" style={{ color: 'hsl(var(--brand-green))' }}>✓</span>
                  <span className="flex-1">{flashMessage}</span>
                  <button
                    type="button"
                    onClick={() => setFlashMessage(null)}
                    aria-label="Dismiss"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                </div>
              )}

              {flashError && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2"
                >
                  <span className="flex-1">{flashError}</span>
                  <button
                    type="button"
                    onClick={() => setFlashError(null)}
                    aria-label="Dismiss"
                    className="text-xs hover:opacity-70"
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium">
                      Password
                    </label>
                    <Link
                      to="/auth/forgot-password"
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {error && (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {submitting ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              <div className="border-t border-border pt-4">
                <p className="text-xs text-muted-foreground mb-2">New to PharmaSync?</p>
                <Link
                  to="/auth/sign-up"
                  className="block w-full text-center rounded-md border-2 px-4 py-2 text-sm font-medium hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{
                    borderColor: 'hsl(var(--brand-teal))',
                    color: 'hsl(var(--brand-teal))',
                  }}
                >
                  Start your pilot →
                </Link>
              </div>
            </div>

            {/* FAQ */}
            <section aria-labelledby="faq-h" className="space-y-2">
              <h2 id="faq-h" className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-2">
                Common questions
              </h2>
              {FAQ_ITEMS.map((item, i) => {
                const open = openFaq === i;
                return (
                  <details
                    key={item.q}
                    open={open}
                    onToggle={(e) => {
                      const target = e.currentTarget as HTMLDetailsElement;
                      setOpenFaq(target.open ? i : null);
                    }}
                    className="rounded-md border border-border bg-card"
                  >
                    <summary className="cursor-pointer list-none px-4 py-2.5 text-sm font-medium flex items-center justify-between hover:bg-muted/30">
                      <span>{item.q}</span>
                      <span
                        className="text-xs"
                        style={{ color: 'hsl(var(--brand-teal))' }}
                        aria-hidden="true"
                      >
                        {open ? '−' : '+'}
                      </span>
                    </summary>
                    <p className="px-4 pb-3 pt-1 text-xs text-muted-foreground leading-relaxed">
                      {item.a}
                    </p>
                  </details>
                );
              })}
            </section>
          </div>
        </div>

        <footer className="mt-16 pt-6 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <Link to="/terms" className="hover:underline">Terms</Link>
          <Link to="/dpa" className="hover:underline">DPA</Link>
          <span className="ml-auto">© PharmaSync — Canadian pharmacy scheduling.</span>
        </footer>
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
      
